import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { jsPDF } from "jspdf";
import { Calendar, ArrowRight, ArrowLeft, Download, Shield, Lightbulb, CheckCircle, AlertTriangle, Clock, FileText, Bell, Home, Key, Users, MessageSquare, Phone, Mail, X, Share2 } from "lucide-react";
import AnonymityBanner from "@/components/AnonymityBanner";
import NextStepsFooter from "@/components/NextStepsFooter";
import "@/styles/govtech.css";

// Milestone calculation logic
const calculateMilestones = (tenancyStartDate, tenancyType) => {
  const startDate = new Date(tenancyStartDate);
  const today = new Date();
  const daysSinceStart = Math.floor((today - startDate) / (1000 * 60 * 60 * 24));
  const monthsSinceStart = Math.floor(daysSinceStart / 30);
  
  const milestones = [];
  
  // Introductory to Secure (11-12 months)
  if (tenancyType === 'introductory' || tenancyType === 'starter') {
    const conversionDate = new Date(startDate);
    conversionDate.setMonth(conversionDate.getMonth() + 12);
    const daysUntilConversion = Math.floor((conversionDate - today) / (1000 * 60 * 60 * 24));
    
    milestones.push({
      id: 'intro_to_secure',
      title: 'Introductory to Secure Tenancy',
      description: 'Your tenancy automatically converts to a Secure Tenancy after 12 months, giving you more rights.',
      targetDate: conversionDate,
      daysRemaining: daysUntilConversion,
      status: daysUntilConversion <= 0 ? 'completed' : daysUntilConversion <= 30 ? 'approaching' : 'upcoming',
      icon: Home,
      color: '#00703C',
      actions: [
        'Ensure rent account is clear',
        'No Anti-Social Behaviour issues recorded',
        'Property maintained in good condition',
        'Respond promptly to tenancy review letters'
      ],
      insight: 'The 11-month mark is critical. Your landlord will review your tenancy before conversion. A clear record means automatic upgrade.'
    });
  }
  
  // 3-Year RTB Eligibility
  const rtbEligibilityDate = new Date(startDate);
  rtbEligibilityDate.setFullYear(rtbEligibilityDate.getFullYear() + 3);
  const daysUntilRTB = Math.floor((rtbEligibilityDate - today) / (1000 * 60 * 60 * 24));
  
  milestones.push({
    id: 'rtb_eligibility',
    title: 'Right to Buy Eligibility',
    description: 'After 3 years as a public sector tenant, you become eligible to buy your home at a discount.',
    targetDate: rtbEligibilityDate,
    daysRemaining: daysUntilRTB,
    status: daysUntilRTB <= 0 ? 'completed' : daysUntilRTB <= 90 ? 'approaching' : 'upcoming',
    icon: Key,
    color: '#00703C',
    actions: [
      'Start checking your credit score',
      'Begin saving for a deposit',
      'Research mortgage options',
      'Request a property valuation estimate'
    ],
    insight: 'At 3 years, you get 35% discount (houses) or 50% (flats). This increases by 1-2% each year up to 70% max.'
  });
  
  // 5-Year Succession Rights Strengthened
  const successionDate = new Date(startDate);
  successionDate.setFullYear(successionDate.getFullYear() + 5);
  const daysUntilSuccession = Math.floor((successionDate - today) / (1000 * 60 * 60 * 24));
  
  milestones.push({
    id: 'succession_strengthened',
    title: 'Enhanced Succession Evidence',
    description: 'After 5+ years, you have extensive evidence of residency for succession purposes.',
    targetDate: successionDate,
    daysRemaining: daysUntilSuccession,
    status: daysUntilSuccession <= 0 ? 'completed' : daysUntilSuccession <= 180 ? 'approaching' : 'upcoming',
    icon: Users,
    color: '#00703C',
    actions: [
      'Keep all utility bills in your name',
      'Maintain electoral roll registration',
      'Keep bank statements showing this address',
      'Document any family members living with you'
    ],
    insight: 'Long-term residency strengthens any future succession claim. Keep records from each year.'
  });
  
  // Annual Rent Review (every April)
  const currentYear = today.getFullYear();
  let nextRentReview = new Date(currentYear, 3, 1); // April 1st
  if (today > nextRentReview) {
    nextRentReview = new Date(currentYear + 1, 3, 1);
  }
  const daysUntilRentReview = Math.floor((nextRentReview - today) / (1000 * 60 * 60 * 24));
  
  milestones.push({
    id: 'rent_review',
    title: 'Annual Rent Review',
    description: 'Social housing rents are typically reviewed annually in April.',
    targetDate: nextRentReview,
    daysRemaining: daysUntilRentReview,
    status: daysUntilRentReview <= 30 ? 'approaching' : 'upcoming',
    icon: Calendar,
    color: '#F47738',
    actions: [
      'Budget for potential rent increase',
      'Check if you qualify for Housing Benefit review',
      'Review your household income for Universal Credit',
      'Contact landlord if you have affordability concerns'
    ],
    insight: 'Rent increases are usually limited to CPI + 1%. If you struggle to afford increases, speak to your landlord early.'
  });
  
  return {
    milestones: milestones.sort((a, b) => a.daysRemaining - b.daysRemaining),
    tenancyAge: {
      days: daysSinceStart,
      months: monthsSinceStart,
      years: Math.floor(monthsSinceStart / 12)
    }
  };
};

// Generate Milestone PDF
const generatePDF = (tenancyData, milestoneData) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const contentWidth = pageWidth - (margin * 2);
  let yPos = 20;
  
  // Header
  doc.setFillColor(0, 112, 60);
  doc.rect(0, 0, pageWidth, 40, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.setFont("helvetica", "bold");
  doc.text("TenURes.co.uk", margin, 25);
  
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text("TENANCY MILESTONE REPORT", margin, 35);
  
  yPos = 55;
  
  // Authority Stamp
  doc.setFillColor(244, 244, 244);
  doc.rect(margin, yPos, contentWidth, 20, 'F');
  doc.setDrawColor(0, 112, 60);
  doc.setLineWidth(1);
  doc.line(margin, yPos, margin, yPos + 20);
  
  doc.setTextColor(51, 51, 51);
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text("TENANCY HEALTH CHECK", margin + 5, yPos + 8);
  doc.setFont("helvetica", "normal");
  doc.text("Verified with 10 Years of Frontline Housing Management Experience", margin + 5, yPos + 15);
  
  yPos += 30;
  
  // Tenancy Summary
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Your Tenancy Summary", margin, yPos);
  yPos += 10;
  
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(`Tenancy Start: ${new Date(tenancyData.startDate).toLocaleDateString('en-GB')}`, margin, yPos);
  yPos += 7;
  doc.text(`Tenancy Type: ${tenancyData.type === 'introductory' ? 'Introductory/Starter' : 'Secure/Assured'}`, margin, yPos);
  yPos += 7;
  doc.text(`Time as Tenant: ${milestoneData.tenancyAge.years} years, ${milestoneData.tenancyAge.months % 12} months`, margin, yPos);
  yPos += 15;
  
  // Milestones
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Your Upcoming Milestones", margin, yPos);
  yPos += 10;
  
  milestoneData.milestones.forEach((milestone, index) => {
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }
    
    // Status color
    const statusColor = milestone.status === 'completed' ? [0, 112, 60] : 
                        milestone.status === 'approaching' ? [244, 119, 56] : [100, 100, 100];
    
    doc.setFillColor(...statusColor);
    doc.rect(margin, yPos, 4, 30, 'F');
    
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(51, 51, 51);
    doc.text(milestone.title, margin + 8, yPos + 7);
    
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    
    const statusText = milestone.status === 'completed' ? 'ACHIEVED' :
                       milestone.status === 'approaching' ? `${milestone.daysRemaining} DAYS REMAINING` :
                       `Target: ${milestone.targetDate.toLocaleDateString('en-GB')}`;
    
    doc.setTextColor(...statusColor);
    doc.text(statusText, margin + 8, yPos + 14);
    
    doc.setTextColor(100, 100, 100);
    const descLines = doc.splitTextToSize(milestone.description, contentWidth - 15);
    doc.text(descLines, margin + 8, yPos + 22);
    
    yPos += 40;
  });
  
  // Action Checklist
  doc.addPage();
  yPos = 20;
  
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(51, 51, 51);
  doc.text("Your Action Checklist", margin, yPos);
  yPos += 15;
  
  milestoneData.milestones.forEach(milestone => {
    if (milestone.status !== 'completed') {
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text(milestone.title, margin, yPos);
      yPos += 8;
      
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      milestone.actions.forEach(action => {
        doc.rect(margin, yPos - 3, 4, 4);
        doc.text(action, margin + 8, yPos);
        yPos += 7;
      });
      yPos += 5;
    }
  });
  
  // Footer
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(
      `Generated by TenURes.co.uk | ${new Date().toLocaleDateString('en-GB')} | Page ${i} of ${pageCount}`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
  }
  
  doc.save("TenURes_Milestone_Report.pdf");
};

// Components
const TenuresLogo = () => (
  <span className="text-xl font-bold text-[#333333]">
    Ten<span className="text-[#00703C] font-extrabold">UR</span>es<span className="text-[#00703C]">.co.uk</span>
  </span>
);

const MilestoneCard = ({ milestone, expanded, onToggle }) => {
  const statusConfig = {
    completed: { bg: 'bg-[#00703C]', text: 'text-white', label: 'Achieved' },
    approaching: { bg: 'bg-[#F47738]', text: 'text-white', label: `${milestone.daysRemaining} days` },
    upcoming: { bg: 'bg-gray-200', text: 'text-gray-700', label: 'Upcoming' }
  };
  
  const status = statusConfig[milestone.status];
  const Icon = milestone.icon;
  
  return (
    <div className={`border ${milestone.status === 'approaching' ? 'border-[#F47738] border-2' : 'border-gray-200'} bg-white`}>
      <div 
        className="p-4 cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${milestone.status === 'completed' ? 'bg-[#00703C]' : 'bg-[#00703C] bg-opacity-10'}`}>
            <Icon className={milestone.status === 'completed' ? 'text-white' : 'text-[#00703C]'} size={24} />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-bold text-[#333333]">{milestone.title}</h3>
              <span className={`text-xs px-2 py-1 rounded ${status.bg} ${status.text}`}>
                {status.label}
              </span>
            </div>
            <p className="text-sm text-gray-600">{milestone.description}</p>
            {milestone.status !== 'completed' && (
              <p className="text-xs text-gray-500 mt-1">
                Target: {milestone.targetDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            )}
          </div>
        </div>
      </div>
      
      {expanded && (
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          {/* Pro Tip */}
          <div className="bg-white border-l-4 border-[#F47738] p-3 mb-4">
            <div className="flex items-center gap-2 mb-1">
              <Lightbulb size={14} className="text-[#F47738]" />
              <span className="text-xs font-semibold text-[#F47738] uppercase">10-Year Insight</span>
            </div>
            <p className="text-sm text-gray-700">{milestone.insight}</p>
          </div>
          
          {/* Action Checklist */}
          <h4 className="font-semibold text-[#333333] mb-2">Action Checklist</h4>
          <ul className="space-y-2">
            {milestone.actions.map((action, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                <div className="w-4 h-4 border border-gray-400 mt-0.5 flex-shrink-0" />
                <span>{action}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default function MilestoneGuardian() {
  const navigate = useNavigate();
  const [step, setStep] = useState('form'); // form, results
  const [tenancyData, setTenancyData] = useState({
    startDate: "",
    type: ""
  });
  const [milestoneData, setMilestoneData] = useState(null);
  const [expandedMilestone, setExpandedMilestone] = useState(null);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderForm, setReminderForm] = useState({
    phone: "",
    email: "",
    name: "",
    method: "sms" // sms or whatsapp
  });
  const [reminderSubmitted, setReminderSubmitted] = useState(false);
  
  const handleCalculate = () => {
    const data = calculateMilestones(tenancyData.startDate, tenancyData.type);
    setMilestoneData(data);
    setStep('results');
    // Auto-expand the first approaching milestone
    const approaching = data.milestones.find(m => m.status === 'approaching');
    if (approaching) setExpandedMilestone(approaching.id);
  };
  
  const handleDownloadPDF = () => {
    generatePDF(tenancyData, milestoneData);
  };
  
  const handleReminderSubmit = () => {
    // Log the reminder request (in production, this would save to backend and schedule SMS/WhatsApp)
    const introMilestone = milestoneData.milestones.find(m => m.id === 'intro_to_secure');
    console.log("[Reminder Request]", {
      ...reminderForm,
      tenancyStart: tenancyData.startDate,
      tenancyType: tenancyData.type,
      milestoneDate: introMilestone?.targetDate,
      daysRemaining: introMilestone?.daysRemaining,
      deliveryMethod: reminderForm.method
    });
    setReminderSubmitted(true);
  };
  
  const handleShare = () => {
    const shareText = "I just checked my tenancy milestones on TenURes - a free tool for social housing tenants to track important dates like RTB eligibility and tenancy conversions. Check yours at:";
    const shareUrl = window.location.origin + "/milestone-guardian";
    
    if (navigator.share) {
      navigator.share({
        title: 'TenURes - Tenancy Milestone Guardian',
        text: shareText,
        url: shareUrl
      });
    } else {
      // Fallback to WhatsApp share
      window.open(`https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`, '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b border-gray-200 bg-white">
        <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
          <button onClick={() => navigate('/resident')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
            <ArrowLeft size={20} />
            <span>Back to Portal</span>
          </button>
          <TenuresLogo />
        </div>
      </nav>
      
      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 py-12">
        {/* Authority Stamp */}
        <div className="authority-stamp mb-8">
          <div className="authority-stamp-icon">
            <Shield size={20} />
          </div>
          <div>
            <p className="font-semibold text-gray-900">Tenancy Milestone Guardian</p>
            <p className="text-sm text-gray-600">Never miss a critical tenancy date</p>
          </div>
        </div>
        
        {step === 'form' ? (
          <div className="govtech-card">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-[#00703C] bg-opacity-10 flex items-center justify-center mx-auto mb-4">
                <Calendar className="text-[#00703C]" size={32} />
              </div>
              <h1 className="govtech-heading-l mb-2">Track Your Tenancy Milestones</h1>
              <p className="text-gray-600">
                Enter your tenancy details and we'll show you key dates and actions to protect your housing rights.
              </p>
            </div>

            <AnonymityBanner />
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-[#333333] mb-2">When did your tenancy start? *</label>
                <input
                  type="date"
                  className="govtech-input"
                  value={tenancyData.startDate}
                  onChange={(e) => setTenancyData({...tenancyData, startDate: e.target.value})}
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-[#333333] mb-2">What type of tenancy do you have? *</label>
                <div className="space-y-3">
                  <div
                    onClick={() => setTenancyData({...tenancyData, type: 'introductory'})}
                    className={`govtech-radio-item ${tenancyData.type === 'introductory' ? 'selected' : ''}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      tenancyData.type === 'introductory' ? 'border-[#00703C] bg-[#00703C]' : 'border-gray-400'
                    }`}>
                      {tenancyData.type === 'introductory' && <div className="w-2 h-2 rounded-full bg-white" />}
                    </div>
                    <div>
                      <span className="font-medium text-gray-900">Introductory / Starter Tenancy</span>
                      <p className="text-sm text-gray-500">Usually the first 12 months of a new Council or Housing Association tenancy</p>
                    </div>
                  </div>
                  
                  <div
                    onClick={() => setTenancyData({...tenancyData, type: 'secure'})}
                    className={`govtech-radio-item ${tenancyData.type === 'secure' ? 'selected' : ''}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      tenancyData.type === 'secure' ? 'border-[#00703C] bg-[#00703C]' : 'border-gray-400'
                    }`}>
                      {tenancyData.type === 'secure' && <div className="w-2 h-2 rounded-full bg-white" />}
                    </div>
                    <div>
                      <span className="font-medium text-gray-900">Secure / Assured Tenancy</span>
                      <p className="text-sm text-gray-500">Full tenancy with stronger legal protections</p>
                    </div>
                  </div>
                  
                  <div
                    onClick={() => setTenancyData({...tenancyData, type: 'unsure'})}
                    className={`govtech-radio-item ${tenancyData.type === 'unsure' ? 'selected' : ''}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      tenancyData.type === 'unsure' ? 'border-[#00703C] bg-[#00703C]' : 'border-gray-400'
                    }`}>
                      {tenancyData.type === 'unsure' && <div className="w-2 h-2 rounded-full bg-white" />}
                    </div>
                    <div>
                      <span className="font-medium text-gray-900">I'm not sure</span>
                      <p className="text-sm text-gray-500">Check your tenancy agreement or ask your landlord</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <button
                onClick={handleCalculate}
                disabled={!tenancyData.startDate || !tenancyData.type}
                className={`govtech-btn govtech-btn-primary w-full ${(!tenancyData.startDate || !tenancyData.type) ? 'opacity-50' : ''}`}
              >
                Show My Milestones <ArrowRight size={18} className="ml-2" />
              </button>
            </div>
          </div>
        ) : (
          <div>
            {/* Tenancy Summary */}
            <div className="bg-[#00703C] text-white p-6 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-200">Your Tenancy Age</p>
                  <p className="text-3xl font-bold">
                    {milestoneData.tenancyAge.years > 0 && `${milestoneData.tenancyAge.years} years `}
                    {milestoneData.tenancyAge.months % 12} months
                  </p>
                </div>
                <Clock className="text-green-200" size={48} />
              </div>
            </div>
            
            {/* Alert for approaching milestones */}
            {milestoneData.milestones.some(m => m.status === 'approaching') && (
              <div className="bg-[#F47738] bg-opacity-10 border border-[#F47738] p-4 mb-6 flex items-start gap-3">
                <Bell className="text-[#F47738] flex-shrink-0" size={24} />
                <div>
                  <p className="font-bold text-[#333333]">Action Required</p>
                  <p className="text-sm text-gray-700">
                    You have milestone(s) approaching. Review the actions below to protect your tenancy rights.
                  </p>
                </div>
              </div>
            )}
            
            {/* Milestones */}
            <h2 className="text-xl font-bold text-[#333333] mb-4">Your Milestones</h2>
            <div className="space-y-4 mb-6">
              {milestoneData.milestones.map((milestone) => (
                <MilestoneCard
                  key={milestone.id}
                  milestone={milestone}
                  expanded={expandedMilestone === milestone.id}
                  onToggle={() => setExpandedMilestone(
                    expandedMilestone === milestone.id ? null : milestone.id
                  )}
                />
              ))}
            </div>
            
            {/* SMS Reminder CTA - Show for introductory tenancies that haven't converted yet */}
            {(tenancyData.type === 'introductory' || tenancyData.type === 'starter') && 
             milestoneData.milestones.find(m => m.id === 'intro_to_secure' && m.status !== 'completed') && (
              <div className="bg-[#00703C] bg-opacity-5 border-2 border-[#00703C] border-dashed p-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-full bg-[#00703C] flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="text-white" size={28} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-[#333333] text-lg mb-2">Never Miss Your Tenancy Conversion</h3>
                    <p className="text-gray-700 mb-4">
                      We'll send you a friendly text message reminder when your Starter Tenancy is about to end, 
                      so you can ensure everything is in order for your upgrade to a Secure Tenancy.
                    </p>
                    {!reminderSubmitted ? (
                      <button
                        onClick={() => setShowReminderModal(true)}
                        className="govtech-btn govtech-btn-primary"
                        data-testid="reminder-btn"
                      >
                        <Bell size={18} className="mr-2" />
                        Remind Me When It's Time
                      </button>
                    ) : (
                      <div className="flex items-center gap-2 text-[#00703C] font-medium">
                        <CheckCircle size={20} />
                        <span>Reminder set! We'll text you when it's time.</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Reminder Modal */}
            {showReminderModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white max-w-md w-full p-6 shadow-xl relative">
                  <button 
                    onClick={() => setShowReminderModal(false)}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                  >
                    <X size={24} />
                  </button>
                  
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 rounded-full bg-[#00703C] bg-opacity-10 flex items-center justify-center mx-auto mb-4">
                      <Bell className="text-[#00703C]" size={32} />
                    </div>
                    <h3 className="text-xl font-bold text-[#333333] mb-2">Set Up Your Reminder</h3>
                    <p className="text-sm text-gray-600">
                      We'll send you a text message 30 days before your Starter Tenancy ends, 
                      giving you time to ensure your rent is clear and everything is in order.
                    </p>
                  </div>
                  
                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="block text-sm font-semibold text-[#333333] mb-2">How would you like to be reminded?</label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setReminderForm({...reminderForm, method: 'sms'})}
                          className={`p-3 border-2 text-sm font-medium transition-all ${
                            reminderForm.method === 'sms' 
                              ? 'border-[#00703C] bg-[#00703C] bg-opacity-5 text-[#00703C]' 
                              : 'border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          <MessageSquare className="mx-auto mb-1" size={20} />
                          SMS Text
                        </button>
                        <button
                          type="button"
                          onClick={() => setReminderForm({...reminderForm, method: 'whatsapp'})}
                          className={`p-3 border-2 text-sm font-medium transition-all ${
                            reminderForm.method === 'whatsapp' 
                              ? 'border-[#25D366] bg-[#25D366] bg-opacity-5 text-[#25D366]' 
                              : 'border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          <svg className="mx-auto mb-1" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                          </svg>
                          WhatsApp
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-[#333333] mb-1">
                        <Phone size={14} className="inline mr-1" />
                        Mobile Number *
                      </label>
                      <input
                        type="tel"
                        className="govtech-input"
                        placeholder="07xxx xxxxxx"
                        value={reminderForm.phone}
                        onChange={(e) => setReminderForm({...reminderForm, phone: e.target.value})}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        We'll send your reminder via {reminderForm.method === 'whatsapp' ? 'WhatsApp' : 'SMS'}
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-[#333333] mb-1">
                        <Mail size={14} className="inline mr-1" />
                        Email (Optional backup)
                      </label>
                      <input
                        type="email"
                        className="govtech-input"
                        placeholder="your@email.com"
                        value={reminderForm.email}
                        onChange={(e) => setReminderForm({...reminderForm, email: e.target.value})}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-[#333333] mb-1">Your Name *</label>
                      <input
                        type="text"
                        className="govtech-input"
                        placeholder="First name"
                        value={reminderForm.name}
                        onChange={(e) => setReminderForm({...reminderForm, name: e.target.value})}
                      />
                    </div>
                  </div>
                  
                  {/* Reminder Details */}
                  <div className="bg-gray-50 p-3 mb-6 text-sm">
                    <p className="font-semibold text-[#333333] mb-1">Your Reminder:</p>
                    <p className="text-gray-600">
                      Tenancy conversion date: {milestoneData.milestones.find(m => m.id === 'intro_to_secure')?.targetDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>
                    <p className="text-gray-600">
                      We'll remind you: ~30 days before
                    </p>
                  </div>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowReminderModal(false)}
                      className="govtech-btn govtech-btn-secondary flex-1"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        handleReminderSubmit();
                        setShowReminderModal(false);
                      }}
                      disabled={!reminderForm.phone || !reminderForm.name}
                      className={`govtech-btn govtech-btn-primary flex-1 ${(!reminderForm.phone || !reminderForm.name) ? 'opacity-50' : ''}`}
                    >
                      Set Reminder
                    </button>
                  </div>
                  
                  <p className="text-xs text-gray-500 text-center mt-4">
                    By setting a reminder, you agree to receive SMS from TenURes.
                  </p>
                </div>
              </div>
            )}

            {/* Next Steps / Call-Deflation */}
            <NextStepsFooter reviewDays={28} teamName="housing team" />

            {/* Actions */}
            <div className="flex flex-col gap-3">
              <button
                onClick={handleDownloadPDF}
                className="govtech-btn govtech-btn-primary w-full"
              >
                <Download size={18} className="mr-2" /> Generate Milestone Evidence Report
              </button>
              <p className="text-xs text-gray-500 text-center">Your Clearly-Documented Tenancy Timeline</p>
              
              <button
                onClick={handleShare}
                className="govtech-btn govtech-btn-secondary w-full"
              >
                <Share2 size={18} className="mr-2" /> Share With a Neighbour
              </button>
              
              <button
                onClick={() => setStep('form')}
                className="text-[#00703C] font-medium text-sm hover:underline text-center"
              >
                <ArrowLeft size={14} className="inline mr-1" /> Update My Details
              </button>
            </div>
          </div>
        )}
      </main>
      
      {/* Footer */}
      <footer className="border-t border-gray-200 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm text-gray-500">
            Milestone dates are calculated based on standard UK housing legislation.
            Check with your landlord for specific policies.
          </p>
          <p className="text-sm text-gray-400 mt-2">
            © {new Date().getFullYear()} Ten<span className="text-[#00703C] font-semibold">UR</span>es.co.uk
          </p>
        </div>
      </footer>
    </div>
  );
}
